/**
 * water_audio.ts — what running and standing water sounds like from HERE (2026-09-20). PURE.
 *
 * Rivers, waterfalls, lakes and the sea were silent: no loop asset existed. With
 * the loops generated (tools/asset-pipeline/gen-world-sounds.mjs) this is the
 * policy that places them. Three techniques from game-audio practice:
 *
 *   THE SPLINE EMITTER   a river is a line, not a point: ONE emitter slides
 *                        along the polyline to the point nearest the listener
 *                        (never a row of emitters phasing against each other).
 *   ENVELOPMENT          a big source heard from inside it is not "to the
 *                        right": as the listener reaches the water a 2D copy of
 *                        the same loop fades in over the positional one
 *                        (equal-power), so wading across a river never flips
 *                        it from one ear to the other.
 *   DISTANCE LAYERS      a waterfall up close is bright spray and transients;
 *                        from far off it is a low-mid rumble. Two loops,
 *                        crossfaded by distance — a low-pass alone makes a
 *                        near sound quiet, not a far one.
 *
 * Every gain here is the CALLER's distance curve: the loops are placed with
 * `distance: 'caller'` (the panner adds direction only — one distance owner).
 */
import { inverseDistanceGain } from './spatial_math.js';

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const smooth = (a: number, b: number, v: number): number => { const t = clamp01((v - a) / (b - a)); return t * t * (3 - 2 * t); };

export const WATER_KEYS = {
  river: 'water_river', fallClose: 'water_fall_close', fallFar: 'water_fall_far', sea: 'water_sea_shore', lake: 'water_lake_lap',
} as const;

// ── the spline emitter ──

export interface Nearest { x: number; z: number; d: number; t: number }
/** The point of segment A→B nearest P (XZ). */
export function closestOnSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): Nearest {
  const vx = bx - ax, vz = bz - az, len2 = vx * vx + vz * vz;
  const t = len2 > 1e-9 ? clamp01(((px - ax) * vx + (pz - az) * vz) / len2) : 0;
  const x = ax + vx * t, z = az + vz * t;
  return { x, z, d: Math.hypot(px - x, pz - z), t };
}

export interface RiverSample { x: number; z: number; width: number; water: number; still: boolean }
export interface RiverEmitter { x: number; y: number; z: number; edgeM: number; width: number }
/**
 * The emitter for a run of consecutive river points (one polyline): the nearest point ON the line, with the width
 * and water level interpolated there. A STILL reach (a river crossing a lake, a mouth at the sea) is not a source —
 * the lake and the shore speak for that water. `edgeM` is the distance to the WATER'S EDGE (0 once you are in it).
 */
export function riverEmitter(line: readonly RiverSample[], px: number, pz: number): RiverEmitter | null {
  let best: RiverEmitter | null = null;
  for (let i = 0; i + 1 < line.length; i++) {
    const a = line[i], b = line[i + 1];
    if (a.still && b.still) continue;
    const n = closestOnSegment(px, pz, a.x, a.z, b.x, b.z);
    const width = a.width + (b.width - a.width) * n.t;
    const edgeM = Math.max(0, n.d - width / 2);
    if (!best || edgeM < best.edgeM) best = { x: n.x, y: a.water + (b.water - a.water) * n.t, z: n.z, edgeM, width };
  }
  return best;
}

export const RIVER_RANGE_M = 120;
export interface WaterMix { gain: number; rate: number; /** 0 = a located source … 1 = all around you. */ near: number }
/** A brook chatters high and quiet; a wide river is deeper and carries. */
export function riverMix(edgeM: number, widthM: number): WaterMix {
  const size = clamp01((widthM - 2) / 26);
  return {
    gain: (0.14 + 0.3 * size) * inverseDistanceGain(edgeM, 5 + 6 * size, RIVER_RANGE_M * (0.6 + 0.4 * size)),
    rate: 1.12 - 0.26 * size,
    near: 1 - smooth(1.5, 9, edgeM),
  };
}

// ── waterfalls ──

export const FALL_RANGE_M = 750;
export interface FallMix { close: number; far: number; rate: number; near: number }
/** `drop` × `width` sizes the fall (a 3 m step over a brook is a trickle; 25 m over a river is a roar). */
export function fallMix(distM: number, dropM: number, widthM: number): FallMix {
  const size = clamp01(Math.sqrt(Math.max(0, dropM) * Math.max(0, widthM)) / 22);
  const level = 0.25 + 0.75 * size;
  return {
    close: level * 0.6 * inverseDistanceGain(distM, 10 + 10 * size, 160 + 140 * size),
    // The far layer fades IN with distance: up close the rumble is already inside the close layer's own spectrum.
    far: level * 0.4 * smooth(18, 80, distM) * inverseDistanceGain(distM, 60 + 60 * size, FALL_RANGE_M * (0.45 + 0.55 * size)),
    rate: 1.08 - 0.2 * size,
    near: 1 - smooth(3, 16 + 10 * size, distM),
  };
}

/** The nearest `max` falls in range, loudest first — each entry keeps its site key (the loop's scope). */
export function pickFalls<T extends { key: string; x: number; y: number; z: number; drop: number; width: number }>(sites: readonly T[], px: number, py: number, pz: number, max = 2): Array<{ site: T; dist: number; mix: FallMix }> {
  const out: Array<{ site: T; dist: number; mix: FallMix }> = [];
  for (const site of sites) {
    const dist = Math.hypot(site.x - px, site.y - py, site.z - pz);
    if (dist >= FALL_RANGE_M) continue;
    const mix = fallMix(dist, site.drop, site.width);
    if (mix.close + mix.far > 0.004) out.push({ site, dist, mix });
  }
  return out.sort((a, b) => (b.mix.close + b.mix.far) - (a.mix.close + a.mix.far)).slice(0, max);
}

// ── shores ──

export type StillKind = 'sea' | 'lake';
export const SHORE_RANGE_M: Record<StillKind, number> = { sea: 170, lake: 60 };
/** Waves on a beach carry, and answer the wind (`seaAmp`: the sea state's amplitude scale, 0.35 … 1.85); a lake only laps. */
export function shoreMix(distM: number, kind: StillKind, seaAmp = 1): WaterMix {
  if (kind === 'sea') {
    const swell = clamp01((seaAmp - 0.35) / 1.5);
    return { gain: (0.16 + 0.34 * swell) * inverseDistanceGain(distM, 12, SHORE_RANGE_M.sea), rate: 0.94 + 0.12 * swell, near: 1 - smooth(2, 14, distM) };
  }
  return { gain: 0.15 * inverseDistanceGain(distM, 5, SHORE_RANGE_M.lake), rate: 1, near: 1 - smooth(1, 7, distM) };
}

/** Ray directions for the shore scan (a fan of `n` bearings). */
export function shoreRays(n = 12): Array<[number, number]> {
  return Array.from({ length: n }, (_, i) => { const a = (i / n) * Math.PI * 2; return [Math.cos(a), Math.sin(a)] as [number, number]; });
}

export interface ShoreHit { kind: StillKind; x: number; z: number; d: number }
/**
 * Walk each ray outward from the listener to the first STILL water of each kind (`kindAt` is the water provider's
 * answer; a river is not a shore). Standing IN the water is distance 0. Returns the nearest hit per kind.
 */
export function scanShore(px: number, pz: number, kindAt: (x: number, z: number) => StillKind | 'river' | null, stepM = 10, maxM = SHORE_RANGE_M.sea): Partial<Record<StillKind, ShoreHit>> {
  const out: Partial<Record<StillKind, ShoreHit>> = {};
  const here = kindAt(px, pz);
  if (here === 'sea' || here === 'lake') out[here] = { kind: here, x: px, z: pz, d: 0 };
  for (const [ux, uz] of shoreRays()) {
    for (let d = stepM; d <= maxM; d += stepM) {
      const k = kindAt(px + ux * d, pz + uz * d);
      if (k !== 'sea' && k !== 'lake') continue;
      // THE EDGE, not the step (2026-09-23): the first wet sample of a 10 m walk sat up to 10 m out in the water, so a
      // lake's 1–7 m surround window could never take an in-between value (half level and to one side, then a jump to
      // all around the moment you stepped in). Three halvings put the edge within ~1.25 m.
      let lo = d - stepM, hi = d;
      for (let i = 0; i < 3; i++) { const m = (lo + hi) / 2; if (kindAt(px + ux * m, pz + uz * m) === k) hi = m; else lo = m; }
      if (hi <= SHORE_RANGE_M[k] && (!out[k] || hi < out[k].d)) out[k] = { kind: k, x: px + ux * hi, z: pz + uz * hi, d: hi };
      break; // the first water along a ray hides whatever lies behind it
    }
  }
  return out;
}

/** Equal-power split of one gain between the located voice and the enveloping one. */
export function envelop(gain: number, near: number): { positional: number; around: number } {
  const n = clamp01(near);
  return { positional: gain * Math.cos(n * Math.PI / 2), around: gain * Math.sin(n * Math.PI / 2) };
}
